import { useEffect, useRef, useState } from 'react';
import type { ClassNode } from '../../../shared/catalog/classes';
import { audio } from '../game/audio';
import { getSettings } from '../game/settings';
import { TankStage, tankImage, type TankLook } from './tankArt';

const lookKey = (look: TankLook) => ('code' in look ? `c${look.code}` : `p${Object.values(look.palette).join('.')}`);

/** Vrai dès que l'élément approche de l'écran (les longues listes ne rendent que ce qui se voit). */
function useNearView<T extends Element>(): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver((e) => e.some((x) => x.isIntersecting) && setNear(true), { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [near]);
  return [ref, near];
}

/** Image fixe d'un tank, rendue comme en jeu (mise en cache par classe, apparence et taille). */
export function TankImage({ cls, look, size, angle, className, alt = '' }: { cls: ClassNode; look: TankLook; size: number; angle?: number; className?: string; alt?: string }) {
  const [ref, near] = useNearView<HTMLSpanElement>();
  const [url, setUrl] = useState<string | null>(null);
  const key = lookKey(look);
  useEffect(() => {
    if (!near) return;
    let live = true;
    tankImage(cls, look, size, { angle })
      .then((u) => live && setUrl(u))
      .catch(() => {});
    return () => {
      live = false;
    };
    // `look` est comparé par sa clé : un objet recréé à chaque rendu ne relance pas le dessin.
  }, [near, cls.id, key, size, angle]);
  return (
    <span ref={ref} className={`tank-img ${className ?? ''}`} style={{ width: size, height: size }}>
      {url && <img src={url} width={size} height={size} alt={alt} draggable={false} />}
    </span>
  );
}

/**
 * Grand aperçu animé : la tourelle suit le pointeur, un clic ou une touche tire (recul, éclair, projectile).
 * Avec « animations réduites », une seule image est dessinée et rien ne bouge.
 */
export function TankStageView({ cls, look, radius, label, className }: { cls: ClassNode; look: TankLook; radius: number; label: string; className?: string }) {
  const host = useRef<HTMLDivElement>(null);
  const stage = useRef<TankStage | null>(null);
  const latest = useRef({ cls, look });
  latest.current = { cls, look };
  const still = getSettings().reducedMotion;
  const key = lookKey(look);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let created: TankStage | null = null;
    let cancelled = false;
    let visible = true;
    const sync = () => created?.setActive(visible && !document.hidden);
    const io = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver((e) => {
      visible = e.some((x) => x.isIntersecting);
      sync();
    });
    io?.observe(el);
    document.addEventListener('visibilitychange', sync);
    void TankStage.create(el, { radius, still, onFire: () => audio.play('uiFire') })
      .then((s) => {
        if (cancelled) {
          s.destroy();
          return;
        }
        created = s;
        stage.current = s;
        s.setTank(latest.current.cls, latest.current.look);
        sync();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      io?.disconnect();
      document.removeEventListener('visibilitychange', sync);
      created?.destroy();
      stage.current = null;
    };
  }, [radius, still]);

  useEffect(() => {
    stage.current?.setTank(cls, look);
  }, [cls.id, key]);

  useEffect(() => {
    const move = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') stage.current?.aimAt(e.clientX, e.clientY);
    };
    window.addEventListener('pointermove', move);
    return () => window.removeEventListener('pointermove', move);
  }, []);

  return (
    <div
      ref={host}
      className={`tank-stage ${className ?? ''}`}
      role="img"
      aria-label={label}
      onPointerDown={(e) => {
        stage.current?.aimAt(e.clientX, e.clientY);
        stage.current?.fire();
      }}
    />
  );
}
